import { Button, cn } from "@dune/ui";
import { ArrowRight } from "lucide-react";
import { UseFormHandleSubmit, useFormContext } from "react-hook-form";
import ActionsDetailsContainer from "../../component/brokers/constructionSites/ActionsDetailsContainer";
import AddressDetailsContainer from "../../component/brokers/constructionSites/AddressDetailsContainer";
import DatesDetailsContainer from "../../component/brokers/constructionSites/DatesDetailsContainer";
import DeclarationsContainer from "../../component/brokers/constructionSites/DeclarationsContainer";
import DetailsDescriptionContainer from "../../component/brokers/constructionSites/DetailsDescriptionContainer";
import OperationDescriptiveBlock from "../../component/brokers/constructionSites/OperationDescriptiveBlock";
import InterventionsDetailsContainer from "../../component/brokers/constructionSites/InterventionsDetailsContainer";
import { ConstructionSiteDetailsFormData } from "../../component/brokers/constructionSites/useConstructionSiteDetailsForm";
import GFANameAndTypeContainer from "../../component/gfa/GFANameAndTypeContainer";
import GFAProcessesTypeContainer from "../../component/gfa/GFAProcessesTypeContainer";
import {
	MainDetailsOperation,
	TotalCostOperation,
} from "./components/tabs/operation";
import { toast } from "sonner";

interface ConstructionSiteDetailsFormProps {
	handleSubmit?: UseFormHandleSubmit<ConstructionSiteDetailsFormData>;
	onSubmit?: (data: ConstructionSiteDetailsFormData) => void;
	onSave?: (
		data: ConstructionSiteDetailsFormData & { isNext?: boolean },
	) => void;
	clientIsSyndic?: boolean;
	isForm: boolean;
	canSave?: boolean;
	isGFA?: boolean;
	currentUserIsClient?: boolean;
	/** When false, the "Obtenir ma tarification" button (step 3) is hidden */
	showOfferStepButton?: boolean;
	isSubmittingDetails?: boolean;
}

const ConstructionSiteDetailsForm = ({
	handleSubmit,
	onSubmit,
	onSave,
	clientIsSyndic = false,
	isForm = true,
	canSave = true,
	isGFA = false,
	currentUserIsClient = false,
	showOfferStepButton = true,
	isSubmittingDetails = false,
}: ConstructionSiteDetailsFormProps) => {
	const { getValues } = useFormContext<ConstructionSiteDetailsFormData>();
	return (
		<div
			className={cn("flex items-center justify-center w-full", isForm && "p-6")}
		>
			<form
				onSubmit={
					handleSubmit && onSubmit
						? handleSubmit(onSubmit, (errors) => {
								toast.warning("Veuillez corriger les erreurs du formulaire");
								console.log(errors);
							})
						: undefined
				}
				className={cn(
					"flex-3 flex-col items-center justify-center",
					isForm && "p-4",
				)}
			>
				<div className="flex flex-col gap-4 w-full">
					<div className="flex flex-row gap-4">
						{isForm && (
							<h1 className="text-[#144C65] text-2xl font-medium">
								Opération à assurer
							</h1>
						)}
					</div>
					{isGFA && <GFANameAndTypeContainer />}
					{!isForm && (
						<>
							<OperationDescriptiveBlock className="mb-6" hideTitle />
							<MainDetailsOperation isDisabled={!isForm} />
							<TotalCostOperation isDisabled={!isForm} />
						</>
					)}
					{isForm && (
						<>
							<AddressDetailsContainer />
							<ActionsDetailsContainer
								hideComplementaryButton
								isGFA={isGFA}
								currentUserIsClient={currentUserIsClient}
								clientIsSyndic={clientIsSyndic}
							/>
						</>
					)}
					{isGFA && <GFAProcessesTypeContainer />}
					<DatesDetailsContainer isDisabled={!isForm} />
					<InterventionsDetailsContainer isDisabled={!isForm} />
					<DeclarationsContainer isDisabled={!isForm} />

					{isForm &&
						(!isGFA ? (
							<div className="self-end flex flex-row gap-4">
								<Button
									className="self-end"
									type="button"
									variant="outlineBlue"
									onClick={() => {
										onSave?.(getValues());
									}}
									disabled={!canSave || isSubmittingDetails}
									isLoading={isSubmittingDetails}
									size="lg"
								>
									Enregistrer et fermer
								</Button>
								{showOfferStepButton && (
									<Button
										className="self-end"
										type="submit"
										variant="default"
										disabled={!canSave || isSubmittingDetails}
										isLoading={isSubmittingDetails}
										size="lg"
									>
										Obtenir ma tarification
										<ArrowRight className="w-4 h-4" />
									</Button>
								)}
							</div>
						) : (
							<div className="self-end flex flex-row gap-4">
								<Button
									className="self-end"
									type="button"
									variant="outline"
									size="lg"
									onClick={() => {
										onSave?.({
											...getValues(),
											isNext: false,
										});
									}}
									disabled={!canSave || isSubmittingDetails}
									isLoading={isSubmittingDetails}
								>
									Enregistrer et fermer
								</Button>
								<Button
									className="self-end"
									type="button"
									variant="default"
									size="lg"
									onClick={() => {
										onSave?.({
											...getValues(),
											isNext: true,
										});
									}}
									disabled={!canSave || isSubmittingDetails}
									isLoading={isSubmittingDetails}
								>
									Suivant
									<ArrowRight className="w-4 h-4" />
								</Button>
							</div>
						))}
				</div>
			</form>
			{isForm && <DetailsDescriptionContainer />}
		</div>
	);
};

export default ConstructionSiteDetailsForm;
